import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { CircularProgress, IconButton } from "@mui/material";
import { VisibilityOffOutlined, VisibilityOutlined } from "@mui/icons-material";
import { ActionButtons, EmptyContainer, FileName, FileRow, IconWrapper, } from "../styled/SidePanel.style";
import { WebMapContext } from "../../pages/sections/web-map/WebMapGoogleMap";
import { SiteUploadIcons } from "../../constants/WebMap";
import { maskString } from "../../utils/helper";
import { deleteUploadedFile, getUploadedFiles } from "../../services/api/mapFileUpload.api";
import DialogWrapper from "../DialogWrapper";
import { toast } from "react-toastify";

export default function UploadFilesSidePanel({ isPanelOpen = false }) {

  const {
    triggerFetchUploadedFiles,
    setSelectedUploadedFile,
    appliedFilters,
    isLoading,
    setIsLoading,
  } = useContext(WebMapContext);

  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [isSidePanelLoading, setIsSidePanelLoading] = useState(false);

  const selectedFileRef = useRef([]);

  const handleRemoveFile = async (id) => {
    try {
      setIsLoading(true);
      const response = await deleteUploadedFile(id);

      if (response) {
        toast.success('File deleted successfully', { theme: 'colored' });
        setSelectedUploadedFile(null);
        selectedFileRef.current = [];
        fetchUploadedFiles();
        setDeleteConfirm('');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelected = (index) => {
    const updatedFiles = uploadedFiles.map((file, i) =>
      i === index ? { ...file, selected: !file.selected } : { ...file }
    );
    const filtered = updatedFiles.filter((file) => file.selected);
    setSelectedUploadedFile(filtered);
    selectedFileRef.current = filtered; // Persist selection
    setUploadedFiles(updatedFiles);
  };

  const fetchUploadedFiles = useCallback(async () => {
    try {
      const { country, year, quarter } = appliedFilters;
      setIsSidePanelLoading(true);
      selectedFileRef.current = [];
      const updatedFiles = await getUploadedFiles(country, year, quarter);
      setUploadedFiles(updatedFiles);
      setSelectedUploadedFile(null);

      // Restore selection
      const restoredFiles = updatedFiles.map((file) => ({
        ...file,
        selected: selectedFileRef.current.some(
          (selectedFile) => selectedFile._id === file._id
        ),
      }));
      setUploadedFiles(restoredFiles);
    } finally {
      setIsSidePanelLoading(false);
    }
  }, [appliedFilters, setSelectedUploadedFile]);

  useEffect(() => {
    fetchUploadedFiles();
  }, [fetchUploadedFiles, triggerFetchUploadedFiles]);

  return (
    <>
      {isPanelOpen && (
        <div style={{ minHeight: 100 }}>
          {isSidePanelLoading ? (
            <EmptyContainer>
              <CircularProgress
                color="inherit"
                style={{ width: "30px", height: "30px" }}
              />
            </EmptyContainer>
          ) : null}

          {!isSidePanelLoading && uploadedFiles.length === 0 ? (
            <EmptyContainer>
              <FileName>{"No files"}</FileName>
            </EmptyContainer>
          ) : null}

          {!isSidePanelLoading &&
            uploadedFiles.map((file, index) => (
              <FileRow
                bgColor={file.selected ? file?.color : "#FFF"}
                key={file?._id}
              >
                {/* File Name and Icon */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <FileName
                    color={file?.color}
                    title={file?.file_name}
                    style={{ marginRight: "8px" }}
                  >{`${index + 1}. `}</FileName>
                  <IconWrapper>
                    {React.cloneElement(
                      SiteUploadIcons[file?.icon] || SiteUploadIcons.pin,
                      { fill: file?.color, stroke: "white", width: 20 }
                    )}
                  </IconWrapper>
                  <FileName
                    color={file?.color}
                    title={file?.file_name}
                    style={{ marginRight: "8px" }}
                  >
                    {maskString(file?.file_name, 18)}
                  </FileName>
                </div>

                {/* Action Buttons */}
                <ActionButtons className="action-buttons">
                  <IconButton
                    aria-label="view"
                    onClick={() => handleSelected(index)}
                  >
                    {file.selected ? (
                      <VisibilityOutlined
                        style={{ color: "grey", fontSize: 17, margin: "4px" }}
                      />
                    ) : (
                      <VisibilityOffOutlined
                        style={{ color: "grey", fontSize: 17, margin: "4px" }}
                      />
                    )}
                  </IconButton>
                  <IconButton
                    aria-label="delete"
                    onClick={() => setDeleteConfirm(file?._id)}
                    style={{ marginLeft: "4px" }}
                  >
                    <img
                      src={"/assets/deleteIcon.png"}
                      alt="Delete Icon"
                      width="16"
                    />
                  </IconButton>
                </ActionButtons>
              </FileRow>
            ))}
        </div>
      )}
      <DialogWrapper
        title={"Confirmation"}
        cancelLabel="Cancel"
        actionLabel={"Confirm"}
        version
        small
        open={deleteConfirm?.length}
        acceptButton={() => !isLoading && handleRemoveFile(deleteConfirm)}
        cancelAction={() => !isLoading && setDeleteConfirm(false)}
        onClose={() => !isLoading && setDeleteConfirm(false)}
      >
        {`Are you sure you want to delete this file?`}
      </DialogWrapper>
    </>
  );
}
